import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

export interface MoveTarget { key: string; label: string; onSelect(): void }

/**
 * "Move to" for one standard (R10, KTD10): a button that opens a list of real buttons, so
 * moving never happens by accident while arrowing through a select.
 */
export function MoveMenu({ label, targets }: { label: string; targets: MoveTarget[] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const items = () => [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  useEffect(() => { if (open) items()[0]?.focus(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!menu.current?.parentElement?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const onKeyDown = (event: KeyboardEvent) => {
    const all = items(); const at = all.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); button.current?.focus(); }
    else if (event.key === 'ArrowDown') { event.preventDefault(); all[(at + 1) % all.length]?.focus(); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); all[(at - 1 + all.length) % all.length]?.focus(); }
  };
  return <div className="move-menu" onKeyDown={onKeyDown}>
    <button ref={button} className="text-button" type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(o => !o)}>Move to</button>
    {open && <ul id={id} ref={menu} role="menu" aria-label={label}>
      {targets.map(t => <li key={t.key} role="none"><button role="menuitem" type="button" onClick={() => { setOpen(false); button.current?.focus(); t.onSelect(); }}>{t.label}</button></li>)}
    </ul>}
  </div>;
}
